import { jsPDF } from "jspdf";
import { GeneratorData } from "@/models/pdfModels";
import { getTableColumFunctions, offset } from "./utils";
import coordinatesMap, { PdfLayoutBlockKeys } from "./coordinatesMap";
import { Colors } from "./colors";
import { LineWidth } from "./lineWidth";
import { ImageDataUrlValues } from "@context/ImageDataUrlContext";

export const generatePdf = (
  imageDataUrls: ImageDataUrlValues["dataUrls"],
): jsPDF => {
  // Default export is a4 paper, portrait, using millimeters for units
  const doc = new jsPDF();
  const withOffset = offsetGenerator(doc, imageDataUrls);

  generateHeader(withOffset("header"));
  generateTeamBlock(withOffset("team2lock"));
  generateMetaInfoBlock(withOffset("metaInfoBlock"));
  generateChallengeTable(withOffset("challengeTable"));
  generateRemainingChallengesBlock(withOffset("remainingChallengesBlock"));
  generateLegendBlock(withOffset("legendBlock"));

  doc.setDocumentProperties({
    title: `challenge-protocol-template`,
    creator: `VBL Challenge Protokoll`,
  });

  return doc;
};

const generateHeader = ({
  doc,
  offset: { x, y },
  imageDataUrls,
}: GeneratorData) => {
  doc.setFont("Arial", "bold");
  doc.setFontSize(10);
  doc.text("Video Challenge Protokoll", x(0), y(0));

  const dataUrl = imageDataUrls["vblLogo"];
  const showVblLogo = false;

  if (dataUrl && showVblLogo) {
    doc.addImage(dataUrl, "png", x(174 - 18), y(-5), 18, 18);
  }
};

const generateTeamBlock = ({ doc, offset: { x, y } }: GeneratorData) => {
  doc.setLineWidth(LineWidth.NORMAL);
  doc.setDrawColor(Colors.BLACK);

  doc.setFillColor(Colors.GREY);
  doc.rect(x(0), y(0), 174, 6, "FD");

  doc.rect(x(0), y(0), 174, 17);
  doc.line(x(87), y(0), x(87), y(17));
  doc.line(x(16), y(6), x(16), y(17));
  doc.line(x(103), y(6), x(103), y(17));

  doc.setFont("Arial", "bold");
  doc.setFontSize(11);
  doc.text("HEIMTEAM", x(42.5), y(4.5), {
    align: "center",
    maxWidth: 85,
  });
  doc.text("GASTTEAM", x(129.5), y(4.5), {
    align: "center",
    maxWidth: 85,
  });
};

const generateMetaInfoBlock = ({ doc, offset: { x, y } }: GeneratorData) => {
  doc.setLineWidth(LineWidth.NORMAL);
  doc.setDrawColor(Colors.BLACK);

  doc.setFillColor(Colors.GREY);
  doc.rect(x(0), y(0), 174, 6, "FD");

  doc.rect(x(0), y(0), 174, 17);
  doc.line(x(26), y(0), x(26), y(17));
  doc.line(x(76), y(0), x(76), y(17));

  doc.setFont("Arial", "bold");
  doc.setFontSize(11);
  doc.text("SPIELNR.", x(13), y(4.5), {
    align: "center",
    maxWidth: 26,
  });
  doc.text("SPIELBEGINN", x(51), y(4.5), {
    align: "center",
    maxWidth: 50,
  });
  doc.text("SPIELORT", x(123), y(4.5), {
    align: "center",
    maxWidth: 98,
  });
};

const generateChallengeTable = ({
  doc,
  offset: { x, y },
  imageDataUrls,
}: GeneratorData) => {
  doc.setLineWidth(LineWidth.NORMAL);
  doc.setDrawColor(Colors.BLACK);

  doc.setFillColor(Colors.GREY);
  doc.rect(x(0), y(0), 174, 22, "FD");

  doc.setFont("Arial", "normal");
  doc.setFontSize(11);

  const { columnWidth, columnStart, columnCenter, columnEnd } =
    getTableColumFunctions();

  for (let i = 0; i <= columnWidth.length; i++) {
    const xCoordinate = x(columnEnd(i));
    const yCoordinate = [2, 8, 9].includes(i) ? y(14) : y(0);

    doc.line(xCoordinate, yCoordinate, xCoordinate, y(142));
  }

  for (let i = 1; i <= 15; i++) {
    doc.line(
      x(0),
      y(22 + i * 8),
      x(columnEnd(columnWidth.length)),
      y(22 + i * 8),
    );

    doc.text(`${i}`, x(columnCenter(0)), y(19.5 + i * 8), {
      align: "center",
      maxWidth: columnWidth[0],
    });
  }

  doc.line(x(columnStart(2)), y(14), x(columnEnd(3)), y(14));
  doc.line(x(columnStart(8)), y(14), x(columnEnd(10)), y(14));

  doc.setFont("Arial", "bold");
  doc.setFontSize(11);

  doc.text("#", x(columnCenter(0)), y(12.5), {
    align: "center",
    maxWidth: columnWidth[0],
  });

  doc.text("Team", x(columnStart(3)), y(8), {
    align: "center",
    maxWidth: columnWidth[1] + columnWidth[2],
  });

  doc.text("Satz", x(columnCenter(3)), y(12.5), {
    align: "center",
    maxWidth: columnWidth[3],
  });

  const scaledCenterColumn4 = columnEnd(4) + columnWidth[4] / 0.9 / 2;
  doc.text("Spielstand vor Challenge (Anpfiff)", x(scaledCenterColumn4), y(5), {
    align: "center",
    maxWidth: columnWidth[4],
    horizontalScale: 0.9,
  });

  const scaledCenterColumn5 = columnEnd(5) + columnWidth[5] / 0.9 / 2;
  doc.text("Spielstand vor Challenge (Abpfiff)", x(scaledCenterColumn5), y(5), {
    align: "center",
    maxWidth: columnWidth[5],
    horizontalScale: 0.9,
  });

  doc.text("Art der Challenge", x(columnCenter(6)), y(12.5), {
    align: "center",
    maxWidth: columnWidth[6],
    horizontalScale: 1,
  });

  const scaledCenterColumn10 = columnEnd(10) + columnWidth[10] / 0.9 / 2;
  doc.text("Spielstand nach Challenge", x(scaledCenterColumn10), y(7.5), {
    align: "center",
    maxWidth: columnWidth[10],
    horizontalScale: 0.9,
  });

  doc.text("Ergebnis der Challenge", x(columnCenter(8)), y(7), {
    align: "center",
    maxWidth: columnWidth[7] + columnWidth[8] + columnWidth[9],
    horizontalScale: 1,
  });

  doc.setFontSize(7);

  if (imageDataUrls?.thumbUp) {
    doc.addImage(
      imageDataUrls.thumbUp,
      "png",
      x(columnStart(8) + 2.5),
      y(15),
      6,
      6,
    );
  } else {
    doc.text("Erflgr.", x(columnCenter(7)), y(18), {
      align: "center",
      maxWidth: columnWidth[7],
    });
  }

  if (imageDataUrls?.thumbDown) {
    doc.addImage(
      imageDataUrls.thumbDown,
      "png",
      x(columnStart(9) + 2.5),
      y(15),
      6,
      6,
    );
  } else {
    doc.text("Nicht Erflgr.", x(columnCenter(8)), y(17), {
      align: "center",
      maxWidth: columnWidth[8],
    });
  }

  if (imageDataUrls?.close && imageDataUrls.radioButtonUnchecked) {
    doc.addImage(
      imageDataUrls.close,
      "png",
      x(columnStart(10) + 2.5),
      y(15),
      6,
      6,
    );
    doc.addImage(
      imageDataUrls.radioButtonUnchecked,
      "png",
      x(columnStart(10) + 2.5),
      y(15),
      6,
      6,
    );
  } else {
    doc.text("Nicht Auflsbr.", x(columnCenter(9)), y(17), {
      align: "center",
      maxWidth: columnWidth[9],
    });
  }
};

const generateRemainingChallengesBlock = ({
  doc,
  offset: { x, y },
  imageDataUrls,
}: GeneratorData) => {
  doc.rect(x(0), y(0), 40, 42);

  doc.setFillColor(Colors.GREY);
  doc.rect(x(0), y(0), 40, 12, "FD");
  doc.line(x(10), y(0), x(10), y(42));
  doc.line(x(25), y(5), x(25), y(42));
  doc.line(x(10), y(5), x(40), y(5));

  doc.setFont("Arial", "bold");
  doc.setFontSize(11);

  doc.text("VERBLEIBENDE CHALLENGES", x(20), y(-6), {
    align: "center",
    maxWidth: 40,
  });

  doc.text("Satz", x(5), y(7), {
    align: "center",
    maxWidth: 10,
  });

  doc.text("Team", x(25), y(4), {
    align: "center",
    maxWidth: 30,
  });

  for (let i = 1; i <= 5; i++) {
    doc.line(x(0), y(12 + i * 6), x(40), y(12 + i * 6));

    doc.setFont("Arial", "normal");
    doc.setFontSize(11);
    doc.text(`${i}`, x(5), y(10.5 + i * 6), { align: "center", maxWidth: 12 });

    doc.circle(x(14.5), y(6.5 + i * 6) + 2.5, 2, "S");
    doc.circle(x(20.5), y(6.5 + i * 6) + 2.5, 2, "S");
    doc.circle(x(29.5), y(6.5 + i * 6) + 2.5, 2, "S");
    doc.circle(x(35.5), y(6.5 + i * 6) + 2.5, 2, "S");
  }
};

const generateLegendBlock = ({
  doc,
  offset: { x, y },
  imageDataUrls,
}: GeneratorData) => {
  doc.setFillColor(Colors.GREY);
  doc.rect(x(0), y(0), 80, 6, "FD");

  doc.setFont("Arial", "bold");
  doc.setFontSize(11);

  doc.text("LEGENDE", x(40), y(-2), {
    align: "center",
    maxWidth: 80,
  });

  doc.text("Symbol", x(8), y(4), { align: "center", maxWidth: 16 });
  doc.text("Bedeutung", x(50), y(4), { align: "center", maxWidth: 64 });

  const legendItems: {
    text: string;
    imageCallback: (yCoordinatey: number) => void;
  }[] = [
    {
      text: "Challenge erfolgreich",
      imageCallback: (yCoordinate) => {
        if (imageDataUrls.thumbUp) {
          doc.addImage(imageDataUrls.thumbUp, "png", x(5.5), yCoordinate, 5, 5);
        } else {
          doc.setFontSize(7);
          doc.text("Erflgr.", x(8), yCoordinate + 4, {
            align: "center",
            maxWidth: 16,
          });
        }
      },
    },
    {
      text: "Challenge nicht erfolgreich",
      imageCallback: (yCoordinate) => {
        if (imageDataUrls.thumbDown) {
          doc.addImage(
            imageDataUrls.thumbDown,
            "png",
            x(5.5),
            yCoordinate,
            5,
            5,
          );
        } else {
          doc.setFontSize(7);
          doc.text("Nicht Erflgr.", x(8), yCoordinate + 2, {
            align: "center",
            maxWidth: 12,
          });
        }
      },
    },
    {
      text: "Challenge nicht auflösbar",
      imageCallback: (yCoordinate) => {
        if (imageDataUrls.close && imageDataUrls.radioButtonUnchecked) {
          doc.addImage(imageDataUrls.close, "png", x(5.5), yCoordinate, 5, 5);
          doc.addImage(
            imageDataUrls.radioButtonUnchecked,
            "png",
            x(5.5),
            yCoordinate,
            5,
            5,
          );
        } else {
          doc.setFontSize(7);
          doc.text("Nicht Auflsbr.", x(8), yCoordinate + 2, {
            align: "center",
            maxWidth: 12,
          });
        }
      },
    },
    {
      text: "Zwei verbleibende Challenges",
      imageCallback: (yCoordinate) => {
        doc.circle(x(5.5), yCoordinate + 2.5, 2, "S");
        doc.circle(x(11.5), yCoordinate + 2.5, 2, "S");
      },
    },
    {
      text: "Eine verbleibende Challenge",
      imageCallback: (yCoordinate) => {
        doc.circle(x(5.5), yCoordinate + 2.5, 2, "S");
        doc.circle(x(5.5), yCoordinate + 2.5, 1, "F");
        doc.circle(x(11.5), yCoordinate + 2.5, 2, "S");
      },
    },
    {
      text: "Keine verbleibenden Challenges",
      imageCallback: (yCoordinate) => {
        doc.circle(x(5.5), yCoordinate + 2.5, 2, "S");
        doc.circle(x(5.5), yCoordinate + 2.5, 1, "F");
        doc.circle(x(11.5), yCoordinate + 2.5, 2, "S");
        doc.circle(x(11.5), yCoordinate + 2.5, 1, "F");
      },
    },
  ];

  doc.setFont("Arial", "normal");

  for (let i = 0; i < legendItems.length; i++) {
    const yCoordinate = y((2 + i) * 6);
    legendItems[i].imageCallback(yCoordinate - 4);
    doc.setFontSize(11);
    doc.text(legendItems[i].text, x(18), yCoordinate);
  }
};

const offsetGenerator =
  (doc: jsPDF, imageDataUrls: ImageDataUrlValues["dataUrls"]) =>
  (layoutBlockKey: PdfLayoutBlockKeys): GeneratorData => {
    const [xOffset, yOffset] = coordinatesMap[layoutBlockKey];
    return { doc, offset: offset(xOffset, yOffset), imageDataUrls };
  };
